<template>
  <div class="ringcentral-agent-view-section mb-4">
    <!-- RingCentral Section Card -->
    <div class="card shadow-sm border-0 bg-light">
      <div class="card-body p-3">
        <!-- Dynamic Agent Selection Chips (Only shown when multiple agents are accessible) -->
        <div class="d-flex flex-wrap align-items-center gap-2 mb-3" v-if="agents && agents.length > 1">
          <span class="text-uppercase fw-bold text-muted me-2" style="font-size: 11px;">Agents:</span>
          <button
            v-for="agent in agents"
            :key="agent"
            class="btn btn-sm chip-btn"
            :class="{ 'btn-dark': selectedAgent === agent, 'btn-light border': selectedAgent !== agent }"
            @click="selectAgent(agent)"
          >
            {{ agent }} <span v-if="managers[agent]">★</span>
          </button>
        </div>

        <div v-if="errorMessage" class="alert alert-warning py-2 px-3 mb-3 small mb-0" role="alert">
          {{ errorMessage }}
          <button type="button" class="btn btn-link btn-sm p-0 ms-2 align-baseline" @click="fetchDashboardData">Retry</button>
        </div>

        <!-- Timeline Strip -->
        <div class="tl-card p-3 mb-3 border rounded bg-white shadow-sm position-relative">
          <div class="section-loader-overlay" v-if="isLoading">
            <span class="loader"></span>
          </div>
          <div class="d-flex align-items-center gap-3">
            <div class="tlday fw-bold text-truncate" style="width: 110px; font-size: 13px;">{{ selectedAgent }}</div>
            <div class="tlbar flex-grow-1 position-relative" style="height: 44px; background: #eef2f5; border: 1px solid #dde4ea; border-radius: 6px; overflow: hidden;">
              <div
                v-for="(blk, idx) in currentTimeline.blocks"
                :key="idx"
                class="blk position-absolute"
                :class="{ 'bg-blue': blk[2] === 0, 'bg-green': blk[2] === 1 }"
                :style="getBlockStyle(blk)"
                :title="getBlockTitle(blk)"
              ></div>
            </div>
            <div class="tlstats text-end" style="width: 200px; font-size: 13px;">
              <div>Talk: <b>{{ formatDuration(currentTimeline.talk) }}</b></div>
              <div class="small text-muted">Calls: <b>{{ currentTimeline.calls }}</b> (Out: {{ currentTimeline.out }})</div>
            </div>
          </div>

          <!-- Timeline Axis Ticks (07:00 to 20:00) -->
          <div class="tlaxis d-flex justify-content-between text-muted small mt-2 px-1" style="margin-left: 110px; margin-right: 200px; font-size: 11px;">
            <span>7:00</span>
            <span>8:00</span>
            <span>9:00</span>
            <span>10:00</span>
            <span>11:00</span>
            <span>12:00</span>
            <span>13:00</span>
            <span>14:00</span>
            <span>15:00</span>
            <span>16:00</span>
            <span>17:00</span>
            <span>18:00</span>
            <span>19:00</span>
            <span>20:00</span>
          </div>
        </div>

        <!-- KPI Cards Grid -->
        <div class="row g-2">
          <div class="col-md-3 col-6">
            <div class="card p-2 text-center border rounded shadow-sm bg-white">
              <div class="small text-muted" style="font-size: 11px;">NET Sales today</div>
              <div class="h4 mb-0 text-success fw-bold">{{ currentKpi.sales_n || 0 }}</div>
            </div>
          </div>
          <div class="col-md-3 col-6">
            <div class="card p-2 text-center border rounded shadow-sm bg-white">
              <div class="small text-muted" style="font-size: 11px;">NET Revenue today</div>
              <div class="h4 mb-0 text-success fw-bold">£{{ formatCurrency(currentKpi.sales_rev || 0) }}</div>
            </div>
          </div>
          <div class="col-md-3 col-6">
            <div class="card p-2 text-center border rounded shadow-sm bg-white">
              <div class="small text-muted" style="font-size: 11px;">Callbacks due</div>
              <div class="h4 mb-0 fw-bold" :class="{ 'text-danger': currentKpi.cb_today > 0 }">{{ currentKpi.cb_today || 0 }}</div>
            </div>
          </div>
          <div class="col-md-3 col-6">
            <div class="card p-2 text-center border rounded shadow-sm bg-white">
              <div class="small text-muted" style="font-size: 11px;">Overdue</div>
              <div class="h4 mb-0 fw-bold" :class="{ 'text-danger': currentKpi.cb_overdue > 0 }">{{ currentKpi.cb_overdue || 0 }}</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import apiClient from '@/axios';
import { getSessionContext } from '@/utils/session-context';

/** Support Laravel flat body and Nest `{ status, data }` wrap. */
const unwrapAgentViewPayload = (body) => {
  if (!body || typeof body !== 'object') return {};
  if (body.agents != null || body.per != null || body.selected_agent != null) {
    return body;
  }
  if (body.data && typeof body.data === 'object' && (body.data.agents != null || body.data.per != null)) {
    return body.data;
  }
  return body.data && typeof body.data === 'object' ? body.data : body;
};

export default {
  name: 'RingCentralAgentView',
  setup() {
    const store = useStore();
    const selectedDate = ref(new Date().toISOString().substring(0, 10));
    const selectedAgent = ref('');
    const agents = ref([]);
    const managers = ref({});
    const rawData = ref({});
    const isLoading = ref(false);
    const errorMessage = ref('');

    const ensureTenantContext = async () => {
      let { subdomain } = getSessionContext();
      if (subdomain) return subdomain;

      try {
        await store.dispatch('auth/fetchCompany');
      } catch (e) {
        // fetchCompany already logs; fall through to re-check session
      }
      subdomain = getSessionContext().subdomain;
      return subdomain;
    };

    const fetchDashboardData = async () => {
      isLoading.value = true;
      errorMessage.value = '';
      try {
        const subdomain = await ensureTenantContext();
        if (!subdomain) {
          errorMessage.value = 'Tenant context missing. Please sign in again or load company profile.';
          rawData.value = {};
          agents.value = [];
          managers.value = {};
          return;
        }

        const response = await apiClient.get('/ringcentral/agent-view', {
          params: {
            date: selectedDate.value,
            agent: selectedAgent.value,
          }
        });
        const payload = unwrapAgentViewPayload(response.data);
        rawData.value = payload || {};
        agents.value = payload?.agents || [];
        managers.value = payload?.managers || {};
        if (payload?.selected_agent) {
          selectedAgent.value = payload.selected_agent;
        }
      } catch (err) {
        console.error('Failed to fetch RingCentral Agent View data:', err);
        const msg = err?.response?.data?.message;
        errorMessage.value = msg || 'Failed to load RingCentral agent view.';
        rawData.value = {};
        agents.value = [];
        managers.value = {};
      } finally {
        isLoading.value = false;
      }
    };

    const selectAgent = (agentName) => {
      selectedAgent.value = agentName;
      fetchDashboardData();
    };

    const currentData = computed(() => {
      return rawData.value?.per?.[selectedAgent.value] || {};
    });

    const currentTimeline = computed(() => {
      return currentData.value?.timeline || { blocks: [], calls: 0, talk: 0, out: 0 };
    });

    const currentKpi = computed(() => {
      return currentData.value?.kpi || {};
    });

    const getBlockStyle = (blk) => {
      const [startSec, dur] = blk;
      const minSec = 25200; // 07:00
      const totalSec = 46800; // 07:00 to 20:00
      const leftPercent = Math.max(0, Math.min(100, ((startSec - minSec) / totalSec) * 100));
      const widthPercent = Math.max(0.3, Math.min(100 - leftPercent, (dur / totalSec) * 100));

      return {
        left: `${leftPercent}%`,
        width: `${widthPercent}%`,
        top: '4px',
        bottom: '4px',
      };
    };

    const getBlockTitle = (blk) => {
      const [startSec, dur, isOut] = blk;
      const type = isOut ? 'Outgoing' : 'Incoming Accepted';
      const h = Math.floor(startSec / 3600).toString().padStart(2, '0');
      const m = Math.floor((startSec % 3600) / 60).toString().padStart(2, '0');
      return `${type} call at ${h}:${m} (Duration: ${dur}s)`;
    };

    const formatDuration = (secs) => {
      if (!secs) return '0m';
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      const s = secs % 60;
      if (h > 0) return `${h}h ${m}m`;
      return `${m}m ${s}s`;
    };

    const formatCurrency = (val) => {
      return (val || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    onMounted(() => {
      fetchDashboardData();
    });

    return {
      selectedDate,
      selectedAgent,
      agents,
      managers,
      isLoading,
      errorMessage,
      currentData,
      currentTimeline,
      currentKpi,
      fetchDashboardData,
      selectAgent,
      getBlockStyle,
      getBlockTitle,
      formatDuration,
      formatCurrency,
    };
  }
};
</script>

<style scoped>
.chip-btn {
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.bg-green {
  background-color: #4CA232 !important;
}

.bg-blue {
  background-color: #2a78d6 !important;
}

.blk {
  min-width: 2px;
  border-radius: 2px;
}

.section-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 0.375rem;
}
</style>
